import React from "react";
import {
  createBrowserRouter,
  IndexRouteObject,
  NonIndexRouteObject,
  Navigate,
} from "react-router-dom";
import { DashboardLayout } from "@/layouts/DashboardLayout";
import { PublicLayout } from "@/layouts/PublicLayout";
import { RouteId } from "@/routes/getRoute";

import { AdoptionAndEngagement } from "@/pages/AdoptionAndEngagement";
import { PredictiveModels } from "@/pages/PredictionModels";
import { ForgotPassword } from "@/pages/ForgotPassword";
import { ResetPassword } from "@/pages/ResetPassword";
import { Components } from "@/pages/Components";
import { Placeholder } from "@/pages/Placeholder";
import { StudentsList } from "@/pages/StudentsList";
import { Login } from "@/pages/Login";
import { OTPVerification } from "@/pages/OTPVerification";
import { Home } from "@/pages/Home";
import { Adoption } from "@/pages/AdoptionAndEngagement/Adoption/Adoption";
import { PublicRoute } from "./PublicRoute";
import { AuthRoute } from "./AuthRoute";
import { i18n } from "../i18n";
import { CommunityToolsOverview } from "@/pages/CommunityToolsOverview";
import { BulkMessageOverview } from "@/pages/BulkMessageOverview";
import { ROUTES } from "./paths";
import { GamesAndLiveEventsOverview } from "@/pages/GamesAndLiveEventsOverview";
import { ZeemeeFunnel } from "@/pages/ZeemeeFunnel";
import { StudentProfilePage } from "@/pages/StudentProfilePage";
import { BulkMessagingPage } from "@/pages/BulkMessaging/index";
import { BasicInformationPage } from "@/pages/BulkMessaging/BasicInformationPage";
import { BulkMessageReviewPage } from "@/pages/BulkMessaging/BulkMessageReviewPage";
import { Chat } from "@/pages/Chat";
import { EditEvent } from "@/pages/EditEvent";
import { ChooseEvent } from "@/pages/EditEvent/chooseEvent";
import { LiveEventSubmit } from "@/pages/EditEvent/LiveEvent/submit";
import { GameSubmit } from "@/pages/EditEvent/Game/submit";
import { ComposeMessage } from "@/pages/BulkMessaging/ComposeMessage";
import { EventDetails } from "@/pages/EventDetails";
import { BulkMessageDetails } from "@/pages/BulkMessaging/BulkMessageDetails";
import { CampaignDetails } from "@/pages/BulkMessaging/BulkMessageDetails/CampaignDetails";
import { TeamManagementOverview } from "@/pages/TeamManagement";
import { EditBulkMessage } from "@/pages/BulkMessaging/EditBulkMessage";
import { NewBulkMessage } from "@/pages/BulkMessaging/NewBulkMessage";
import { FeedPostOverview } from "@/pages/FeedPosts/FeedPostOverview";
import { NewInvite } from "@/pages/TeamManagement/NewInvite";
import { InviteForm } from "@/pages/TeamManagement/InviteForm";
import { TeamManagementPage } from "@/pages/TeamManagement/Overview";
import { Engagement } from "@/pages/AdoptionAndEngagement/Engagement";
import { AcceptInvite } from "@/pages/Invitatations/AcceptInvite";
import { DataManagementOverview } from "@/pages/DataManagement";
import { FeedPostDetails } from "@/pages/FeedPosts/FeedPostDetails";
import { PostDetails } from "@/pages/FeedPosts/FeedPostDetails/PostDetails";
import { Results } from "@/pages/FeedPosts/FeedPostDetails/Results";
import { FeedPostsPage } from "@/pages/FeedPosts";
import { ChannelRequestForm } from "@/pages/ChannelRequestForm";
import { PredictiveModelPage } from "@/pages/PredictionModels/PredictiveModelPage";
import { ManageProfile } from "@/pages/ManageProfile";
import { EditProfile } from "@/pages/ManageProfile/EditProfile";
import { EditContactInfo } from "@/pages/ManageProfile/EditContactInfo";
import { EditFunFacts } from "@/pages/ManageProfile/EditFunFacts";
import { FatalError } from "@/components/FatalError";

export interface RouteHandle {
  name?: string;
  prefixOnly?: boolean;
  routeId?: RouteId;
  allowFilters?: boolean;
}

export type CustomIndexRouteObject = Omit<IndexRouteObject, "handle"> & {
  handle?: RouteHandle;
};

export type CustomNonIndexRouteObject = Omit<
  NonIndexRouteObject,
  "handle" | "children"
> & {
  handle?: RouteHandle;
  children?: (CustomNonIndexRouteObject | CustomIndexRouteObject)[];
};

export type NavRouteObject = CustomIndexRouteObject | CustomNonIndexRouteObject;

// Navigation routes - these appear in the sidebar
export const navRoutes: NavRouteObject[] = [
  // TODO: Re-enable when we have these pages made
  // {
  //   path: ROUTES.RISE.BASE,
  //   handle: { name: i18n.t("nav.rise") },
  //   children: [
  //     {
  //       path: ROUTES.RISE.STRATEGY,
  //       handle: {
  //         name: i18n.t("nav.strategy"),
  //         routeId: "strategy",
  //       },
  //       Component: () => <div></div>,
  //     },
  //     {
  //       path: ROUTES.RISE.IMPACT,
  //       handle: {
  //         name: i18n.t("nav.impact"),
  //         routeId: "impact",
  //       },
  //       Component: () => <div></div>,
  //     },
  //   ],
  // },
  {
    handle: {
      name: i18n.t("nav.home"),
      routeId: "home",
    },
    index: true,
    Component: Home,
  },
  {
    path: ROUTES.STUDENTS,
    handle: {
      name: i18n.t("nav.students"),
      allowFilters: true,
      routeId: "students",
    },
    Component: StudentsList,
  },
  {
    path: ROUTES.ANALYTICS.BASE,
    handle: { name: i18n.t("nav.data"), prefixOnly: true },
    children: [
      {
        index: true,
        handle: {
          routeId: "funnel",
        },
        Component: ZeemeeFunnel,
      },
      {
        path: ROUTES.ANALYTICS.FUNNEL,
        handle: {
          name: i18n.t("nav.funnel"),
          routeId: "funnel",
          allowFilters: true,
        },
        Component: ZeemeeFunnel,
      },
      {
        path: ROUTES.ANALYTICS.ADOPTION,
        handle: {
          name: i18n.t("nav.adoption"),
          routeId: "adoption",
        },
        Component: AdoptionAndEngagement,
        children: [
          {
            index: true,
            Component: Adoption,
          },
          {
            path: ROUTES.ANALYTICS.ENGAGEMENT_DATA,
            handle: {
              name: i18n.t("adoption.tabs.engagementData"),
              routeId: "engagementData",
            },
            Component: Engagement,
          },
          {
            path: ROUTES.ANALYTICS.ENGAGEMENT_INDEX,
            handle: {
              name: i18n.t("adoption.tabs.commitmentIndex"),
              routeId: "engagementIndex",
            },
            Component: function EngagementIndex() {
              return <PredictiveModelPage modelType="Commit" />;
            },
          },
        ],
      },
      {
        path: ROUTES.ANALYTICS.PREDICTIVE_MODELS,
        handle: {
          name: i18n.t("nav.predictiveModels"),
          routeId: "predictiveModels",
        },
        children: [
          {
            index: true,
            Component: PredictiveModels,
          },
          {
            path: ROUTES.ANALYTICS.APPLY_INDEX,
            handle: {
              name: i18n.t("predictiveModels.tabs.applyIndex"),
              routeId: "applyIndex",
            },
            Component: PredictiveModels,
          },
          {
            path: ROUTES.ANALYTICS.COMMIT_INDEX,
            handle: {
              name: i18n.t("predictiveModels.tabs.commitIndex"),
              routeId: "commitIndex",
            },
            Component: PredictiveModels,
          },
          {
            path: ROUTES.ANALYTICS.MELT_INDEX,
            handle: {
              name: i18n.t("predictiveModels.tabs.meltIndex"),
              routeId: "meltIndex",
            },
            Component: PredictiveModels,
          },
          {
            path: ROUTES.ANALYTICS.YIELD_INDEX,
            handle: {
              name: i18n.t("predictiveModels.tabs.yieldIndex"),
              routeId: "yieldIndex",
            },
            Component: PredictiveModels,
          },
          {
            path: ROUTES.ANALYTICS.RETAIN_INDEX,
            handle: {
              name: i18n.t("predictiveModels.tabs.retainIndex"),
              routeId: "retainIndex",
            },
            Component: PredictiveModels,
          },
        ],
      },
    ],
  },
  {
    path: ROUTES.COMMUNITY_TOOLS.BASE,
    handle: { name: i18n.t("nav.communityTools"), prefixOnly: true },
    children: [
      {
        index: true,
        Component: CommunityToolsOverview,
      },
      {
        path: ROUTES.COMMUNITY_TOOLS.OVERVIEW,
        handle: {
          name: i18n.t("nav.overview"),
          routeId: "communityTools",
        },
        Component: CommunityToolsOverview,
      },
      {
        path: ROUTES.COMMUNITY_TOOLS.FEED_POSTS.BASE,
        handle: {
          name: i18n.t("nav.feedPosts"),
        },
        Component: FeedPostsPage,
        children: [
          {
            index: true,
            Component: FeedPostOverview,
          },
          {
            path: `:feedPostId/${ROUTES.COMMUNITY_TOOLS.FEED_POSTS.DETAILS}`,
            handle: {
              name: i18n.t("feedPostDetails.title"),
              routeId: "feedPostDetails",
            },
            Component: FeedPostDetails,
            children: [
              {
                index: true,
                Component: PostDetails,
              },
              {
                path: "results",
                handle: {
                  name: i18n.t("feedPostDetails.details.tabs.results"),
                  routeId: "feedPostResults",
                },
                Component: Results,
              },
            ],
          },
        ],
      },
      {
        path: ROUTES.COMMUNITY_TOOLS.GAMES_AND_LIVE_EVENTS,
        handle: {
          name: i18n.t("nav.chatGames"),
          routeId: "gamesAndLiveEvents",
        },
        Component: GamesAndLiveEventsOverview,
      },
      {
        path: ROUTES.COMMUNITY_TOOLS.BULK_MESSAGING.BASE,
        handle: { name: i18n.t("nav.bulkMessaging") },
        Component: BulkMessagingPage,
        children: [
          {
            index: true,
            handle: { routeId: "bulkMessaging" },
            Component: BulkMessageOverview,
          },
          {
            path: ROUTES.COMMUNITY_TOOLS.BULK_MESSAGING.SEND_NOW,
            handle: {
              name: i18n.t("bulkMessaging.title"),
              routeId: "createBulkMessage",
            },
            Component: NewBulkMessage,
            children: [
              {
                index: true,
                Component: () => (
                  <Navigate
                    to={ROUTES.COMMUNITY_TOOLS.BULK_MESSAGING.BASIC_INFORMATION}
                    replace
                  />
                ),
              },
              {
                path: ROUTES.COMMUNITY_TOOLS.BULK_MESSAGING.BASIC_INFORMATION,
                handle: {
                  name: i18n.t("bulkMessaging.basicInformation.title"),
                  routeId: "bulkMessageBasicInfo",
                },
                Component: BasicInformationPage,
              },
              {
                path: ROUTES.COMMUNITY_TOOLS.BULK_MESSAGING.COMPOSE_MESSAGE,
                handle: {
                  name: i18n.t("bulkMessaging.composeMessage.title"),
                  routeId: "bulkMessageCompose",
                },
                Component: ComposeMessage,
              },
              {
                path: ROUTES.COMMUNITY_TOOLS.BULK_MESSAGING.REVIEW,
                handle: {
                  name: i18n.t("bulkMessaging.review.title"),
                  routeId: "bulkMessageReview",
                },
                Component: BulkMessageReviewPage,
              },
            ],
          },
          {
            path: `:bulkMessageId/${ROUTES.COMMUNITY_TOOLS.BULK_MESSAGING.EDIT}`,
            handle: {
              name: i18n.t("bulkMessaging.editTitle"),
              routeId: "editBulkMessaging",
            },
            Component: EditBulkMessage,
            children: [
              {
                index: true,
                Component: () => (
                  <Navigate
                    to={ROUTES.COMMUNITY_TOOLS.BULK_MESSAGING.BASIC_INFORMATION}
                    replace
                  />
                ),
              },
              {
                path: ROUTES.COMMUNITY_TOOLS.BULK_MESSAGING.BASIC_INFORMATION,
                handle: {
                  name: i18n.t("bulkMessaging.basicInformation.title"),
                  routeId: "bulkMessageBasicInfo",
                },
                Component: BasicInformationPage,
              },
              {
                path: ROUTES.COMMUNITY_TOOLS.BULK_MESSAGING.COMPOSE_MESSAGE,
                handle: {
                  name: i18n.t("bulkMessaging.composeMessage.title"),
                  routeId: "bulkMessageCompose",
                },
                Component: ComposeMessage,
              },
              {
                path: ROUTES.COMMUNITY_TOOLS.BULK_MESSAGING.REVIEW,
                handle: {
                  name: i18n.t("bulkMessaging.review.title"),
                  routeId: "bulkMessageReview",
                },
                Component: BulkMessageReviewPage,
              },
            ],
          },
          {
            path: `:bulkMessageId/${ROUTES.COMMUNITY_TOOLS.BULK_MESSAGING.DETAILS}`,
            handle: { name: i18n.t("bulkMessaging.details.title") },
            Component: BulkMessageDetails,
            children: [
              {
                index: true,
                handle: { routeId: "bulkMessageDetails" },
                Component: CampaignDetails,
              },
              {
                path: ROUTES.COMMUNITY_TOOLS.BULK_MESSAGING.CAMPAIGN_DETAILS,
                handle: {
                  name: i18n.t("bulkMessaging.details.campaignDetails"),
                  routeId: "bulkMessageCampaignDetails",
                },
                Component: CampaignDetails,
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: ROUTES.COMMUNITY_MANAGEMENT.BASE,
    handle: { name: i18n.t("nav.communityManagement"), prefixOnly: true },
    children: [
      {
        path: ROUTES.COMMUNITY_MANAGEMENT.MANAGE_TEAM.BASE,
        handle: {
          name: i18n.t("nav.teamManagement"),
          routeId: "teammates",
        },
        Component: TeamManagementPage,
        children: [
          {
            index: true,
            Component: TeamManagementOverview,
          },
          {
            path: ROUTES.COMMUNITY_MANAGEMENT.MANAGE_TEAM.SEND_INVITE,
            handle: {
              name: i18n.t("teamManagement.invite"),
              routeId: "sendInvite",
            },
            Component: NewInvite,
            children: [
              {
                index: true,
                Component: () => (
                  <Navigate
                    to={ROUTES.COMMUNITY_MANAGEMENT.MANAGE_TEAM.DETAILS}
                    replace
                  />
                ),
              },
              {
                path: ROUTES.COMMUNITY_MANAGEMENT.MANAGE_TEAM.DETAILS,
                handle: {
                  name: i18n.t("teamManagement.inviteForm.details.tab"),
                },
                Component: InviteForm,
              },
            ],
          },
        ],
      },
      {
        path: ROUTES.DATA_MANAGEMENT.BASE,
        handle: {
          name: i18n.t("nav.dataManagement"),
          routeId: "dataManagement",
        },
        Component: DataManagementOverview,
      },
      {
        path: ROUTES.COMMUNITY_MANAGEMENT.CHANNEL_REQUEST,
        handle: {
          name: i18n.t("nav.channelRequest"),
          routeId: "channelRequest",
        },
        Component: ChannelRequestForm,
      },
    ],
  },
];

// Additional routes that don't appear in navigation
const additionalRoutes: NavRouteObject[] = [
  {
    path: ROUTES.STUDENT_PROFILE,
    handle: {
      name: i18n.t("nav.studentProfile"),
      routeId: "student",
    },
    Component: StudentProfilePage,
  },
  {
    path: ROUTES.CHAT.INDEX,
    handle: {
      name: i18n.t("nav.chat"),
      routeId: "chat",
    },
    Component: Chat,
  },
  {
    path: ROUTES.CHAT.SELECTED_CHAT,
    handle: {
      name: i18n.t("nav.chat"),
      routeId: "selectedChat",
    },
    Component: Chat,
  },

  {
    path: ROUTES.COMMUNITY_TOOLS.BASE,
    handle: { name: i18n.t("nav.communityTools") },
    children: [
      {
        path: ROUTES.COMMUNITY_TOOLS.GAMES_AND_LIVE_EVENTS,
        handle: { name: i18n.t("nav.chatGames") },
        children: [
          {
            path: ":eventId",
            handle: { name: i18n.t("nav.eventDetails") },
            Component: EventDetails,
          },
          {
            path: `:eventId/${ROUTES.COMMUNITY_TOOLS.EVENT_DETAILS.DETAILS}`,
            handle: {
              name: i18n.t("nav.eventDetails"),
              routeId: "eventDetails",
            },
            Component: EventDetails,
          },
          {
            path: `:eventId/${ROUTES.COMMUNITY_TOOLS.EVENT_DETAILS.RESULTS}`,
            handle: {
              name: i18n.t("nav.eventDetails"),
              routeId: "eventResults",
            },
            Component: EventDetails,
          },
        ],
      },
      {
        path: ROUTES.COMMUNITY_TOOLS.EDIT_EVENT,
        handle: {
          name: i18n.t("editEvent.newEvent.title"),
          routeId: "editEvent",
        },
        Component: EditEvent,
        children: [
          {
            index: true,
            Component: ChooseEvent,
          },
          {
            path: ROUTES.COMMUNITY_TOOLS.LIVE_EVENT,
            handle: {
              name: i18n.t("editEvent.tabs.liveEvent"),
              routeId: "editLiveEvent",
            },
            Component: LiveEventSubmit,
          },
          {
            path: ROUTES.COMMUNITY_TOOLS.GAME,
            handle: {
              name: i18n.t("editEvent.tabs.gameEvent"),
              routeId: "editGame",
            },
            Component: GameSubmit,
          },
        ],
      },
    ],
  },
  {
    path: `${ROUTES.COMMUNITY_TOOLS.BASE}/bulk-messaging/:bulkMessageId/${ROUTES.COMMUNITY_TOOLS.BULK_MESSAGING.DETAILS}`,
    handle: { name: i18n.t("bulkMessaging.details.title") },
    Component: BulkMessageDetails,
    children: [
      { index: true, Component: CampaignDetails },
      {
        path: ROUTES.COMMUNITY_TOOLS.BULK_MESSAGING.CAMPAIGN_DETAILS,
        handle: {
          name: i18n.t("bulkMessaging.details.campaignDetails"),
        },
        Component: CampaignDetails,
      },
    ],
  },
  {
    path: ROUTES.MANAGE_PROFILE.BASE,
    handle: {
      name: i18n.t("nav.manageProfile"),
      routeId: "manageProfile",
    },
    Component: ManageProfile,
    children: [
      {
        path: ROUTES.MANAGE_PROFILE.YOUR_PROFILE,
        handle: {
          name: i18n.t("nav.editProfile"),
          routeId: "editProfile",
        },
        Component: EditProfile,
      },
      {
        path: ROUTES.MANAGE_PROFILE.FUN_FACTS,
        handle: {
          name: i18n.t("manageProfile.tabs.funFacts"),
          routeId: "editFunFacts",
        },
        Component: EditFunFacts,
      },
      {
        path: ROUTES.MANAGE_PROFILE.CONTACT_INFO,
        handle: {
          name: i18n.t("manageProfile.tabs.contactInfo"),
          routeId: "editContactInfo",
        },
        Component: EditContactInfo,
      },
    ],
  },

  // Routes hidden from navigation for initial release - accessible for development purposes
  // These will appear in the nav in future releases
  {
    handle: {
      name: i18n.t("nav.peerMessaging"),
      routeId: "peerMessaging",
    },
    path: ROUTES.COMMUNITY_TOOLS.PEER_MESSAGING,
    Component: Placeholder,
  },
  {
    handle: {
      name: i18n.t("nav.competitorMatch"),
      routeId: "competitorMatch",
    },
    path: ROUTES.COMMUNITY_TOOLS.COMPETITOR_MATCH,
    Component: Placeholder,
  },
  {
    handle: {
      name: i18n.t("nav.dab"),
      routeId: "dynamicActionButton",
    },
    path: ROUTES.COMMUNITY_TOOLS.DAB,
    Component: Placeholder,
  },
  {
    path: ROUTES.COMMUNITY_MANAGEMENT.UPDATE_SCHOOL_PROFILE,
    handle: {
      name: i18n.t("nav.updateSchool"),
      routeId: "updateSchoolProfile",
    },
    Component: Placeholder,
  },
  {
    path: ROUTES.COMMUNITY_MANAGEMENT.MANAGE_CHANNELS,
    handle: {
      name: i18n.t("nav.channelManagement"),
      routeId: "manageChannels",
    },
    Component: Placeholder,
  },
  {
    path: ROUTES.SETTINGS.BASE,
    handle: { name: i18n.t("nav.settings"), prefixOnly: true },
    children: [
      {
        path: ROUTES.SETTINGS.MANAGE_ACCOUNTS,
        handle: {
          name: i18n.t("nav.manageAccounts"),
          routeId: "manageAccounts",
        },
        Component: Placeholder,
      },
      {
        path: ROUTES.SETTINGS.NOTIFICATION_SETTINGS,
        handle: {
          name: i18n.t("nav.notificationSettings"),
          routeId: "notificationSettings",
        },
        Component: Placeholder,
      },
    ],
  },
  // Development purposes only
  {
    path: ROUTES.COMPONENTS,
    handle: {
      name: i18n.t("nav.components"),
      routeId: "components",
    },
    Component: Components,
  },
];

export const router = createBrowserRouter([
  {
    Component: PublicRoute,
    children: [
      {
        Component: PublicLayout,
        children: [
          {
            path: ROUTES.PUBLIC.LOGIN,
            Component: Login,
          },
          {
            path: ROUTES.PUBLIC.FORGOT_PASSWORD,
            Component: ForgotPassword,
          },
          {
            path: ROUTES.PUBLIC.RESET_PASSWORD,
            Component: ResetPassword,
          },
          {
            path: ROUTES.PUBLIC.OTP_VERIFICATION,
            Component: OTPVerification,
          },
          {
            path: ROUTES.PUBLIC.ACCEPT_INVITE,
            Component: AcceptInvite,
          },
        ],
      },
    ],
  },
  {
    Component: AuthRoute,
    children: [
      {
        Component: DashboardLayout,
        path: ROUTES.ORGANIZATION,
        children: [
          // Navigation routes (appear in side navigation)
          ...navRoutes,
          // Additional routes (don't appear in side navigation)
          ...additionalRoutes,
        ],
      },
      // this will redirect with user's default org
      {
        Component: DashboardLayout,
        path: "/",
      },
    ],
  },
  // Catch-all route for 404 errors
  {
    path: "*",
    element: (
      <div className="flex h-screen w-screen items-center justify-center">
        <FatalError />
      </div>
    ),
  },
]);
